import { motion } from 'motion/react'
import { mq, pxToRem, sizes } from 'src/theme'
import styled, { css, keyframes } from 'styled-components'

import Burger from '../Burger'
import HeaderExternal from '../HeaderExternal'
import HeaderSearch from '../HeaderSearch'
import LangSwitch from '../LangSwitch'
import Menu from '../Menu'
import MotionLogo from '../MotionLogo'
import Logo from '../Logo'

import { Scrolled } from './types'

const slideDown = keyframes`
  0% {
    translate: 0 -100%;
  }
  100% {
    translate: 0 0%;
  }
`
const slideUp = keyframes`
  0% {
    translate: 0 0%;
  }
  100% {
    translate: 0 -100%;
  }
`

export const Container = styled.header<{ $scrolled?: boolean }>`
  --background: transparent;
  --border-color: transparent;
  position: fixed;
  inset: 0 0 auto 0;
  z-index: ${({ theme }) => theme.zIndexes.navigation};
  display: flex;
  flex-flow: row;
  background: var(--background);
  transition:
    background-color 0.3s ease-in-out,
    border-color 0.1s 0s;
  border-block-end: 1px solid var(--border-color);
  min-height: var(--g_height__header);
  &[data-scrolled=${Scrolled.Up}] {
    animation: ${slideDown} 0.3s ease-in-out 1 forwards;
  }
  &[data-scrolled=${Scrolled.Down}] {
    animation: ${slideUp} 0.3s ease-in-out 1 forwards;
  }
  &[data-open],
  &[data-scrolled=${Scrolled.Reduced}],
  &[data-scrolled=${Scrolled.Down}],
  &[data-scrolled=${Scrolled.Up}] {
    --background: ${({ theme }) => theme.colors.white};
    --border-color: ${({ theme }) => theme.colors.greyAA};
    transition:
      background-color 0.3s ease-in-out,
      border-color 0.6s 0.3s;
  }
  &[data-open] {
    z-index: ${({ theme }) => theme.zIndexes.modal};
  }
  @media ${mq(sizes.big)} {
    gap: ${pxToRem(116)};
    &[data-open]:not([data-scrolled=${Scrolled.Reduced}]):not([data-scrolled=${Scrolled.Down}]):not([data-scrolled=${Scrolled.Up}]) {
      --background: transparent;
      --border-color: transparent;
    }
  }
`
export const Left = styled(motion.div)`
  display: flex;
  flex-flow: row;
  width: ${pxToRem(140)};
  transition: opacity 0.3s ease-in-out;
  transform-origin: top left;
  @media ${mq(sizes.big)} {
    width: calc(380 / 1440 * 100vw);
    // another damn exception to deal with z-index and specific 💩
    ${Container}[data-open]:not([data-scrolled=${Scrolled.Reduced}]):not([data-scrolled=${Scrolled.Down}]):not([data-scrolled=${Scrolled.Up}]) & {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`

const baseLogoStyles = css`
  display: block;
  flex: 0 0 auto;
  width: ${pxToRem(140)};
  transform-origin: 0 0;
  transition:
    transform 0.3s ease-in-out,
    width 0.3s ease-in-out;
  @media ${mq(sizes.big)} {
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: 0;
    width: calc(380 / 1440 * 100vw);
    ${Container}[data-scrolled=${Scrolled.Reduced}] &,
    ${Container}[data-scrolled=${Scrolled.Down}] &,
    ${Container}[data-scrolled=${Scrolled.Up}] & {
      width: ${pxToRem(190)};
    }
  }
`

export const _Logo = styled(Logo)`
  ${baseLogoStyles}
`

export const _MotionLogo = styled(MotionLogo)`
  ${baseLogoStyles}
`
export const Right = styled(motion.div)`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row;
  justify-content: flex-end;
  border-inline-start: 1px solid var(--border-color);

  @media ${mq(sizes.big)} {
    justify-content: flex-start;
    transition: border-color 0.1s 0s;
    ${Container}.is-scrolled & {
      transition: border-color 0.6s 0.3s;
    }
  }
`

export const MenuWrapper = styled(motion.div)`
  display: flex;
`

export const DesktopLang = styled(LangSwitch)`
  display: none;
  @media ${mq(sizes.big)} {
    display: flex;
    // not a border because border create silly spaces that do not agree with the client
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      inset-inline-start: auto;
      z-index: 50;
      width: 1px;
      background: var(--border-color);
      transition: background-color 0.1s 0s;
    }
    ${Container}.is-scrolled &::after {
      transition: background-color 0.6s 0.3s;
    }
    // another damn exception to deal with z-index and specific 💩
    ${Container}[data-open]:not([data-scrolled=${Scrolled.Reduced}]):not([data-scrolled=${Scrolled.Down}]):not([data-scrolled=${Scrolled.Up}]) & {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`
export const _HeaderSearch = styled(HeaderSearch)`
  transition:
    border-color 0.1s 0s,
    opacity 0.3s ease-in-out;
  & form {
    border-inline-end: 1px solid var(--border-color);
  }
  & form [hidden] {
    border-color: transparent;
  }
  ${Container}.is-scrolled & {
    transition:
      border-color 0.6s 0.3s,
      opacity 0.3s ease-in-out;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    inset-inline-start: auto;
    z-index: 50;
    background: var(--border-color);
    width: 1px;
    transition: background-color 0.1s 0s;
  }
  ${Container}.is-scrolled &::after {
    transition: background-color 0.6s 0.3s;
  }
  @media ${mq(sizes.big)} {
    // another damn exception to deal with z-index and specific 💩
    ${Container}[data-open]:not([data-scrolled=${Scrolled.Reduced}]):not([data-scrolled=${Scrolled.Down}]):not([data-scrolled=${Scrolled.Up}]) & {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`
export const Separator = styled.span`
  margin-inline-start: auto;
`
export const DesktopExternal = styled(HeaderExternal)`
  display: none;
  @media ${mq(sizes.big)} {
    display: flex;
    padding-inline: ${pxToRem(32)};
    transition: opacity 0.3s ease-in-out;
    // another damn exception to deal with z-index and specific 💩
    ${Container}[data-open]:not([data-scrolled=${Scrolled.Reduced}]):not([data-scrolled=${Scrolled.Down}]):not([data-scrolled=${Scrolled.Up}]) & {
      opacity: 0.5;
      pointer-events: none;
    }
  }
`
export const _Burger = styled(Burger)`
  &[aria-expanded='true'] {
    position: relative;
    z-index: ${({ theme }) => theme.zIndexes.modal + 1};
  }
  @media ${mq(sizes.big)} {
    // another 💩 pseudo-element to replace a border
    &::after {
      content: '';
      position: absolute;
      inset: 100% 0 auto 0;
      z-index: 50;
      height: 1px;
      background: transparent;
      transition: background-color 0.1s 0s;
    }
    &[aria-expanded='true']::after {
      background: ${({ theme }) => theme.colors.greyAA};
      transition: background-color 0.1s 0s;
    }
  }
`
export const Bottom = styled.div`
  position: fixed;
  bottom: 0;
  inset: auto 0 0 0;
  z-index: ${({ theme }) => theme.zIndexes.modal};
  display: flex;
  min-height: var(--g_height__bottom, ${pxToRem(48)});
  border-block-start: 1px solid ${({ theme }) => theme.colors.greyAA};
  background: ${({ theme }) => theme.colors.white};
  &:empty {
    display: none;
  }
  & > :last-child {
    margin-inline-start: auto;
  }
  @media ${mq(sizes.big)} {
    display: none;
  }
`
export const MobileLang = styled(LangSwitch)`
  @media ${mq(sizes.big)} {
    display: none;
  }
`
export const MobileExternal = styled(HeaderExternal)`
  padding-inline: ${pxToRem(16)};
`
export const _Menu = styled(Menu)``
export const _Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndexes.modal - 1};
  pointer-events: none;
  opacity: 0.5;
  &[hidden] {
    display: block;
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    transition:
      opacity 0.3s ease-in-out,
      visibility 0s 0.3s;
  }
  ${Container}[data-scrolled=${Scrolled.Down}] &,
  ${Container}[data-scrolled=${Scrolled.Up}] & {
    inset-block-start: var(--g_height__header);
  }
`
